import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { toast, ToastContainer } from 'react-toastify';
import { useRef } from 'react';
import { FaTimes } from 'react-icons/fa';

import 'react-toastify/dist/ReactToastify.css';
import Sidebar from './Sidebar';
import TopNavbar from '../top-navbar/TopNav';
import '../../styles/employee/education-check.css';
import { getEducationInfo, saveEducation } from '../../services/employee/api';

const EducationInfo = ({ candidateId, educationInfos, onBack }) => {
    const [educationEntries, setEducationEntries] = useState([]);
    const [educationProofFiles, setEducationProofFiles] = useState({}); // index -> File
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [overallVerificationStatus, setOverallVerificationStatus] = useState(false);
    const [overallRemarks, setOverallRemarks] = useState('');
const fileRefs = React.useRef({});

    const token = localStorage.getItem('bgc_token');

    const basename = (path) => {
        if (!path) return '';
        try {
            const u = new URL(path);
            return u.pathname.split('/').pop();
        } catch {
            return (path || '').split('/').pop();
        }
    };

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            setError(null);
            try {
            if (!candidateId) {
                setError("Candidate ID is missing.");
                return;
            }

            let backendData = [];
            if (token) {
                const resp = await getEducationInfo(candidateId);
                backendData = resp.data?.data || [];
            }

            const sourceData =
                backendData.length > 0 ? backendData : educationInfos;
            const mapped = sourceData.map((e) => ({
                educationId: e.educationId,
                educationLevel: e.educationLevel || "",
                enrollmentNumber: e.enrollmentNumber ?? e.enrollment ?? "",
                enrolledYear: e.enrolledYear || "",
                passingYear: e.passingYear ?? e.passOutYear ?? "",
                qualification: e.qualification || "",
                specialization: e.specialization || "",
                instituteName: e.instituteName || "",
                educationProofFileName: basename(
                e.educationFileUrl || e.educationProofFileName || ""
                ),
                educationFileUrl: e.educationFileUrl || null,
                candidateId: e.candidateId || candidateId,
            }));

            setEducationEntries(mapped);
            setErrors(mapped.map(() => ({ file: "" })));
            setOverallRemarks(backendData[0]?.remarks || "");
            setOverallVerificationStatus(false);
            } catch (err) {
            setError(
                err.response?.data?.message ||
                "Failed to load education info."
            );
            } finally {
            setLoading(false);
            }
        };

        init();
        }, [candidateId, educationInfos, token]);

    const handleOverallRemarksChange = (e) => setOverallRemarks(e.target.value);

    // const handleFileChange = (e, index) => {
    //     const file = e.target.files?.[0];
    //     if (file) {
    //         setEducationProofFiles(prev => ({ ...prev, [index]: file }));
    //         setEducationEntries(prev =>
    //             prev.map((entry, i) =>
    //                 i === index
    //                     ? { ...entry, educationProofFileName: file.name }
    //                     : entry
    //             )
    //         );
    //         setErrors(prev => prev.map((er, i) => (i === index ? { ...er, file: '' } : er)));
    //     }
    // };

   const handleFileChange = (e, index) => {
    setErrors([])
  const file = e.target.files[0];
  if (!file) return;

  const updatedEntries = [...educationEntries];
  updatedEntries[index].file = file;

  // Generate temporary preview URL
  updatedEntries[index].educationFileUrl = URL.createObjectURL(file);

  // Store the actual file name
  updatedEntries[index].educationProofFileName = file.name;

  setEducationEntries(updatedEntries);

  // Keep file for form submission
  setEducationProofFiles(prev => ({
    ...prev,
    [index]: file
  }));
};
const triggerFileUpload = (index) => {
  fileRefs.current[index]?.click();
};

const removeEducationFile = (index) => {
  const updatedEntries = [...educationEntries];
  updatedEntries[index].educationFileUrl = null;
  updatedEntries[index].educationProofFileName = '';
  delete educationProofFiles[index];

  setEducationEntries(updatedEntries);
  setEducationProofFiles({ ...educationProofFiles });
};


   /** Validation Logic */
// Line 77
const validateForm = () => {
    let isValid = true;

    // 1. Remarks are mandatory
    if (!overallRemarks.trim()) {
        toast.error('Overall remarks are required.');
        isValid = false;
    }

    // 2. Each education entry must have at least one proof (uploaded file OR existing API URL)
    const newErrors = educationEntries.map((entry, index) => {
        // FIX: properly check if proof exists
        const hasProof =
            (entry.educationProofFileName && entry.educationProofFileName.trim() !== '') ||
            educationProofFiles[index];

        if (!hasProof) {
            isValid = false;
            return { file: 'Education proof is required for this entry.' };
        }
        return { file: '' };
    });
    setErrors(newErrors);

    if (!isValid && overallRemarks.trim()) {
        // Show general toast only if remarks are OK
        toast.error('Please upload at least one proof for each education entry.');
    }

    return isValid;
};
// Line 95


    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            if (!token) {
            throw new Error("Authentication token not found. Please log in.");
            }

            const formData = new FormData();
            formData.append("candidateId", String(candidateId));
            formData.append("isVerified", String(overallVerificationStatus));
            formData.append("remarks", overallRemarks);

            const payload = educationEntries.map(
            ({ educationProofFileName, educationFileUrl, ...rest }) => rest
            );
            formData.append("educationData", JSON.stringify(payload));

            educationEntries.forEach((entry, idx) => {
            if (educationProofFiles[idx]) {
                formData.append("files", educationProofFiles[idx]);
                formData.append("fileEnrollments", entry.educationLevel);
            } else if (entry.educationProofFileName) {
                formData.append("existingFiles", entry.educationProofFileName);
                formData.append("fileEnrollments", entry.educationLevel);
            }
            });

            const response = await saveEducation(formData);
            const result = response.data;
            toast.success("Education info submitted successfully!");
            onBack();
        } catch (err) {
            const message = err.response?.data?.message || err.message || "Failed to submit education info";
            setError(message);
            toast.error("Error: " + message);
        }
        };

    const handleCancel = () => onBack();

    if (loading) return <div>Loading education info...</div>;
    if (error) return <div className="error-message">{error}</div>;

    return (
        <div className="dashboard-app-container">
            <Sidebar />
            <div className="dashboard-right-panel">
                <TopNavbar />
                <div className="education-info-container">
                    <h2>Education Check</h2>
                    <form onSubmit={handleSubmit}>
                        {educationEntries.map((entry, index) => (
                            <div key={entry.educationId || index} className="education-details-card">
                                <h3>Education Details - {entry.educationLevel}</h3>

                                <div className="form-row">
                                    <div className="emp-form-group">
                                        <label>Enrollment No</label>
                                        <input type="text" value={entry.enrollmentNumber || ''} disabled />
                                    </div>
                                    <div className="emp-form-group">
                                        <label>Year of Enrollment</label>
                                        <input type="text" value={entry.enrolledYear || ''} disabled />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="emp-form-group">
                                        <label>Year of Passout</label>
                                        <input type="text" value={entry.passingYear || ''} disabled />
                                    </div>
                                    <div className="emp-form-group">
                                        <label>Qualification</label>
                                        <input type="text" value={entry.qualification || ''} disabled />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="emp-form-group">
                                        <label>Specialization</label>
                                        <input type="text" value={entry.specialization || ''} disabled />
                                    </div>
                                    <div className="emp-form-group full-width">
                                        <label>Institute</label>
                                        <input type="text" value={entry.instituteName || ''} disabled />
                                    </div>
                                </div>

      <div className="form-row">
  <div className="emp-form-group full-width">
    <label>Education Proof</label>


    {entry.educationProofFileName && !educationProofFiles[index] ? (
  <a
    href={entry.educationFileUrl}
    target="_blank"
    rel="noopener noreferrer"
    className="file-link"
  >
  </a>
) : (
  <span className="file-name-display"></span>
)}


  <div className="upload-certificate-group">

  <input
    type="file"
    ref={(el) => (fileRefs.current[index] = el)}
    style={{ display: "none" }}
    accept="image/jpeg,image/png,application/pdf"
    onChange={(e) => handleFileChange(e, index)}
  />

  <div
    className="upload-certificate-button"
    onClick={() => triggerFileUpload(index)}
  >
    Drag & Drop or Click to Upload
  </div>

  {(entry.educationProofFileName || educationProofFiles[index]) && (
    <div className="file-list">
      <span className="file-list-item">
        {entry.educationProofFileName ||
          entry.educationFileUrl?.split('/').pop()}
        <FaTimes
          className="remove-file-icon"
          onClick={() => removeEducationFile(index)}
        />
      </span>
    </div>
  )}

  {errors[index]?.file && (
    <p className="error-text">{errors[index].file}</p>
  )}

  <p className="upload-certificate-note">
    * File must be PDF / JPG / PNG and less than 5MB
  </p>

</div>



    {errors[index]?.file && <p className="error-text">{errors[index].file}</p>}
  </div>
</div>




                            </div>
                        ))}

                        <div className="overall-controls-section">
                            <div className="form-row">
                                <div className="emp-form-group full-width">
                                    <label>Overall Remarks</label>
                                    <textarea
                                        name="overallRemarks"
                                        value={overallRemarks}
                                        onChange={handleOverallRemarksChange}
                                        placeholder="Type here..."
                                    ></textarea>
                                </div>
                            </div>

                            <div className="emp-form-group checkbox-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={overallVerificationStatus}
                                        onChange={(e) => setOverallVerificationStatus(e.target.checked)}
                                    /> Verification Status
                                </label>
                                <p className="verification-note">
                            ⚠️ Once verified, this step will be locked and you cannot return to modify the education details.
                        </p>
                            </div>
                        </div>

                        <div className="form-actions">
                            <button type="button" onClick={handleCancel} className="cancel-button">Cancel</button>
                            <button type="submit" className="submit-button">Submit</button>
                        </div>
                    </form>
                </div>
            </div>
            {/* Toast notifications */}
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover />
        </div>
    );
};

EducationInfo.propTypes = {
    candidateId: PropTypes.number.isRequired,
    educationInfos: PropTypes.array.isRequired,
    onBack: PropTypes.func.isRequired,
};

export default EducationInfo;